import { FeedbackFormOptions, useFeedbackForm } from 'hooks/useFeedbackForm'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, styled, Typography } from 'opensolar-ui'
import { ChipSelection } from 'projectSections/sections/design/sideDrawer/autoDesign/V2/components/ChipSelection'
import { ThumbsButtons } from 'projectSections/sections/design/sideDrawer/autoDesign/V2/components/ThumbsButtons'
import React from 'react'
import { useTranslate } from 'react-admin'
import { FeedbackChipOption } from './BaseFeedback'

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const HeaderRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-bottom: 16px;
`

const ReviewText = styled(Typography)`
  color: rgba(0, 0, 0, 0.8);
  font-size: 16px;
  font-weight: 500;
  text-align: center;
`

const CUSTOM_CHIP_KEY = 'Something Else'

export interface FeedbackModalConfig {
  title: string
  questionText: string
  thumbsUpTooltip: string
  thumbsDownTooltip: string
  positiveFeedbackOptions?: string[] | FeedbackChipOption[]
  negativeFeedbackOptions: string[] | FeedbackChipOption[]
  customFeedbackPlaceholder: string
  successTitle: string
  successMessage: string
  closeButtonText: string
}

export interface FeedbackModalProps {
  open: boolean
  onClose: () => void
  config: FeedbackModalConfig
  feedbackOptions: FeedbackFormOptions
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ open, onClose, config, feedbackOptions }) => {
  const translate = useTranslate()
  const { state, actions, isSubmitting, setCustomFeedbackText } = useFeedbackForm({
    ...feedbackOptions,
    onSuccess: (skipIsSubmitted, feedbackId) => {
      feedbackOptions.onSuccess?.(skipIsSubmitted, feedbackId)
      if (!skipIsSubmitted) {
        setTimeout(() => {
          handleClose()
        }, 2000)
      }
    },
  })

  const handleThumbsClick = async (isPositiveValue: boolean) => {
    await actions.handleThumbsClick(isPositiveValue)
  }

  const handleChipSubmit = async () => {
    await actions.handleChipSubmit()
  }

  const handleCancel = () => {
    actions.handleCancel()
  }

  const handleClose = () => {
    onClose()
    // Reset state after modal closes to prevent flickering
    setTimeout(() => {
      actions.reset()
    }, 100)
  }

  const getFeedbackOptions = () => {
    const options =
      state.isPositive && config.positiveFeedbackOptions
        ? config.positiveFeedbackOptions
        : config.negativeFeedbackOptions

    if (Array.isArray(options) && typeof options[0] === 'string') {
      return (options as string[]).map((option) => ({ key: option, label: option }))
    }

    return options as FeedbackChipOption[]
  }

  const getQuestionText = () => {
    return (
      (state.isPositive && config.positiveFeedbackOptions
        ? translate('What did you like?')
        : translate('What went wrong?')) +
      ' ' +
      translate('Select all that apply:')
    )
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{translate(config.title)}</DialogTitle>

      <DialogContent>
        <Container>
          {state.isSubmitted ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <Typography variant="h6" color="primary" gutterBottom>
                {translate(config.successTitle)}
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ textAlign: 'center' }}>
                {translate(config.successMessage)}
              </Typography>
            </Box>
          ) : (
            <>
              <HeaderRow>
                <ReviewText variant="body1">{translate(config.questionText)}</ReviewText>
              </HeaderRow>

              <Box display="flex" justifyContent="center" mb={3}>
                <ThumbsButtons
                  isPositive={state.isPositive}
                  onThumbsClick={handleThumbsClick}
                  thumbsUpTooltipKey={config.thumbsUpTooltip}
                  thumbsDownTooltipKey={config.thumbsDownTooltip}
                  size="large"
                />
              </Box>

              {state.showChipSelection && (
                <ChipSelection
                  selectedChips={state.selectedChips}
                  onChipToggle={actions.handleChipToggle}
                  onSubmit={handleChipSubmit}
                  onCancel={handleCancel}
                  isSubmitting={isSubmitting}
                  feedbackOptions={getFeedbackOptions().map((option) => option.key)}
                  questionText={getQuestionText()}
                  customFeedback={{
                    enabled: true,
                    text: state.customFeedbackText,
                    placeholder: config.customFeedbackPlaceholder,
                    rows: 3,
                    customChipKey: CUSTOM_CHIP_KEY,
                    onChange: setCustomFeedbackText,
                  }}
                />
              )}
            </>
          )}
        </Container>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          {translate(config.closeButtonText)}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
